import { PriceRecommendations } from './types'

export const MINIMUM_TRHRESHOLD_FOR_RELEVANT_PRICE = 0.2

export const getMinimumThresholdResults = (predictions: PriceRecommendations[]): PriceRecommendations[] => {
  return predictions.filter(rec => parseFloat(rec.relevance) >= MINIMUM_TRHRESHOLD_FOR_RELEVANT_PRICE)
}

// This method will ensure that given a array of PriceRecommandation results, we will provide an array of prices(sold for, starting or BIN) that are above the minimum releveance threshold
export const getMinimumThresholdPrices = (
  predictions: PriceRecommendations[],
  limit: number,
  startingPrice?: boolean,
  binPrice?: boolean
): number[] => {
  return getMinimumThresholdResults(predictions)
    .map(rec => {
      if (binPrice) return parseFloat(rec.buyNowPrice)
      if (startingPrice) return parseFloat(rec.startingPrice)
      return parseFloat(rec.price)
    })
    .slice(0, limit)
    .filter(p => p !== null && p !== undefined && !Number.isNaN(p) && p !== 0)
}

export const updatePricePredictionAnalytics = (state, e, marketKey): [boolean, any] => {
  let analytics = state?.product?.priceRecommendation?.analytics || {}

  if (
    (marketKey === 'shopifyListing' && e.name === 'price') ||
    (marketKey === 'goodwillfindsListing' && Array.isArray(e) && e[0].name === 'price')
  ) {
    const tracked = marketKey === 'goodwillfindsListing' ? e[0].name : e.name
    const trackedValue = marketKey === 'goodwillfindsListing' ? e[0].value : e.value
    if (analytics?.averagePredictions?.mean) {
      if (analytics.clickedViewHistoricalListings) {
        const pricingFieldUpdated = state.product?.priceRecommendation?.analytics.pricingFieldUpdated || {}
        const updated = pricingFieldUpdated[marketKey] || []
        pricingFieldUpdated[marketKey] = Array.from(new Set([...updated, tracked]))
        analytics = { ...analytics, pricingFieldUpdated }
      }
      const averagePriceUsed = state.product?.priceRecommendation?.analytics.averagePriceUsed || {}
      const averagePriceUsedTenPercent = state.product?.priceRecommendation?.analytics.averagePriceUsedTenPercent || {}

      if (parseFloat(trackedValue) === analytics?.averagePredictions?.mean) {
        averagePriceUsed[marketKey] = true
      } else {
        averagePriceUsed[marketKey] = false
        const mean = analytics?.averagePredictions?.mean

        averagePriceUsedTenPercent[marketKey] =
          parseFloat(trackedValue) >= mean - mean * 0.1 && parseFloat(trackedValue) <= mean + mean * 0.1
      }
      analytics = { ...analytics, averagePriceUsed, averagePriceUsedTenPercent }
    }
    return [
      true,
      {
        product: { ...state.product, priceRecommendation: { ...state.product?.priceRecommendation, analytics } }
      }
    ]
  }

  if (
    e.name === 'buyingFormat' ||
    e.name === 'clickedViewHistoricalListings' ||
    e.name === 'averagePredictions' ||
    e.name === 'startingPrice' ||
    e.name === 'startingBid' ||
    e.name === 'buyItNowPrice' ||
    e.name === 'buyNowPrice' ||
    e.name === 'reservePrice' ||
    e.name === 'handlingPrice' ||
    e.name === 'compareAtPrice'
  ) {
    if (e.name === 'averagePredictions') {
      analytics.averagePriceProvided = e.value.mean > 0
      analytics.averagePriceProvidedBIN = e.value.buyItNowPrice > 0
      analytics.averagePriceProvidedStarting = e.value.startingPrice > 0
    }
    if (
      analytics.clickedViewHistoricalListings &&
      // these are all the fields we want to track if they have been updated after they have viewed our recommendations
      (e.name === 'startingPrice' ||
        e.name === 'startingBid' ||
        e.name === 'buyItNowPrice' ||
        e.name === 'buyNowPrice' ||
        e.name === 'reservePrice' ||
        e.name === 'handlingPrice' ||
        e.name === 'compareAtPrice')
    ) {
      const pricingFieldUpdated = state.product?.priceRecommendation?.analytics.pricingFieldUpdated || {}
      const updated = pricingFieldUpdated[marketKey] || []
      pricingFieldUpdated[marketKey] = Array.from(new Set([...updated, e.name]))
      analytics = { ...analytics, pricingFieldUpdated }
    }

    if (
      e.name === 'startingPrice' ||
      e.name === 'buyItNowPrice' ||
      e.name === 'buyNowPrice' ||
      e.name === 'startingBid'
    ) {
      if (e.name === 'startingPrice' || e.name === 'startingBid') {
        const averagePriceUsedSoldForStarting =
          state.product?.priceRecommendation?.analytics.averagePriceUsedSoldForStarting || {}
        const averagePriceUsedStarting = state.product?.priceRecommendation?.analytics.averagePriceUsedStarting || {}

        const averagePriceUsedSoldForStartingTenPercent =
          state.product?.priceRecommendation?.analytics.averagePriceUsedSoldForStartingTenPercent || {}
        const averagePriceUsedStartingTenPercent =
          state.product?.priceRecommendation?.analytics.averagePriceUsedStartingTenPercent || {}

        const mean = parseFloat(analytics?.averagePredictions?.mean)
        const averageStarting = parseFloat(analytics?.averagePredictions?.startingPrice)

        averagePriceUsedSoldForStarting[marketKey] = mean === parseFloat(e.value)
        averagePriceUsedStarting[marketKey] = averageStarting === parseFloat(e.value)

        if (mean > 0)
          averagePriceUsedSoldForStartingTenPercent[marketKey] =
            parseFloat(e.value) <= mean + mean * 0.1 && parseFloat(e.value) >= mean - mean * 0.1

        if (averageStarting > 0)
          averagePriceUsedStartingTenPercent[marketKey] =
            parseFloat(e.value) <= averageStarting + averageStarting * 0.1 &&
            parseFloat(e.value) >= averageStarting - averageStarting * 0.1

        analytics = {
          ...analytics,
          averagePriceUsedStarting,
          averagePriceUsedSoldForStarting,
          averagePriceUsedStartingTenPercent,
          averagePriceUsedSoldForStartingTenPercent
        }
      } else {
        const averagePriceUsedSoldForBIN =
          state.product?.priceRecommendation?.analytics.averagePriceUsedSoldForBIN || {}
        const averagePriceUsedBIN = state.product?.priceRecommendation?.analytics.averagePriceUsedBIN || {}
        const averagePriceUsedSoldForBINTenPercent =
          state.product?.priceRecommendation?.analytics.averagePriceUsedSoldForBINTenPercent || {}
        const averagePriceUsedBINTenPercent =
          state.product?.priceRecommendation?.analytics.averagePriceUsedBINTenPercent || {}

        const mean = parseFloat(analytics?.averagePredictions?.mean)
        const averageBIN = parseFloat(analytics?.averagePredictions?.buyItNowPrice)

        averagePriceUsedSoldForBIN[marketKey] = mean === parseFloat(e.value)
        averagePriceUsedBIN[marketKey] = averageBIN === parseFloat(e.value)

        if (mean > 0)
          averagePriceUsedSoldForBINTenPercent[marketKey] =
            parseFloat(e.value) <= mean + mean * 0.1 && parseFloat(e.value) >= mean - mean * 0.1

        if (averageBIN > 0)
          averagePriceUsedBINTenPercent[marketKey] =
            parseFloat(e.value) <= averageBIN + averageBIN * 0.1 && parseFloat(e.value) >= averageBIN - averageBIN * 0.1

        analytics.buyingFormat = analytics.buyingFormat || {}
        if (!(e.name === 'startingPrice' || e.name === 'startingBid')) {
          if (analytics.buyingFormat[marketKey] === 'Auction') {
            if (e.value > 0) {
              analytics.buyingFormat[marketKey] = 'Combo'
            }
          }
          if (analytics.buyingFormat[marketKey] === 'Combo') {
            if (e.value === 0 || e.value === '0') {
              analytics.buyingFormat[marketKey] = 'Auction'
            }
          }
        }

        analytics = {
          ...analytics,
          averagePriceUsedBIN,
          averagePriceUsedSoldForBIN,
          averagePriceUsedBINTenPercent,
          averagePriceUsedSoldForBINTenPercent
        }
      }
    } else if (e.name === 'buyingFormat') {
      let buyingFormat

      analytics.buyingFormat = analytics.buyingFormat || {}

      if (marketKey === 'shopgoodwillListing') {
        if (e.value.startingPrice && e.value.binPrice) {
          buyingFormat = 'Combo'
        } else if (e.value.startingPrice) buyingFormat = 'Auction'
        else buyingFormat = 'BuyItNow'

        analytics.buyingFormat[marketKey] = buyingFormat
      } else if (marketKey === 'ebayListing') {
        if (e.value.listingType === 'FixedPriceItem') {
          analytics.buyingFormat[marketKey] = 'BuyItNow'
        } else {
          analytics.buyingFormat[marketKey] = 'Auction'
        }
      } else {
        analytics.buyingFormat[marketKey] = e.value.listingType
      }
    } else {
      analytics[e.name] = e.value
    }
    return [
      true,
      {
        product: { ...state.product, priceRecommendation: { ...state.product?.priceRecommendation, analytics } }
      }
    ]
  }
  return [false, {}]
}
